import React, { useMemo } from 'react';
import { Autocomplete, Box, TextField, Typography } from '@mui/material';
import ct from 'countries-and-timezones';

// Mirrors the Schedule Meeting picker on /companies/{company_id}: every IANA
// timezone enriched with country names + UTC offset, searchable by either.
const buildTimezoneOptions = () => {
  try {
    const all       = ct.getAllTimezones();
    const countries = ct.getAllCountries();
    const list = Object.keys(all).map((tzName) => {
      const tz = all[tzName];
      const countryNames = (tz.countries || [])
        .map((cc) => (countries[cc] ? countries[cc].name : ''))
        .filter(Boolean)
        .join(', ');
      const offset       = tz.utcOffset || 0;
      const hours        = Math.floor(Math.abs(offset) / 60);
      const mins         = Math.abs(offset) % 60;
      const sign         = offset >= 0 ? '+' : '-';
      const utcLabel     = mins > 0
        ? `UTC${sign}${hours}:${String(mins).padStart(2, '0')}`
        : `UTC${sign}${hours}`;
      const searchText = [
        tzName.toLowerCase().replace(/_/g, ' '),
        countryNames.toLowerCase(),
        utcLabel.toLowerCase(),
        tz.aliasOf ? tz.aliasOf.toLowerCase() : '',
      ].filter(Boolean).join(' ');
      return {
        value:      tzName,
        label:      `${tzName.replace(/_/g, ' ')} ${countryNames ? `(${countryNames})` : ''} - ${utcLabel}`,
        countries:  countryNames,
        offset,
        searchText,
      };
    });
    return list.sort((a, b) => (a.offset - b.offset) || a.value.localeCompare(b.value));
  } catch (err) {
    return [{
      value: 'Asia/Kolkata',
      label: 'Asia/Kolkata (India) - UTC+5:30',
      countries: 'India',
      offset: 330,
      searchText: 'asia kolkata india utc+5:30',
    }];
  }
};

const _filter = (options, { inputValue }) => {
  if (!inputValue) return options;
  const q = inputValue.toLowerCase().trim();
  const matched = options.filter((opt) => opt?.searchText?.includes(q));
  // Country-name match wins over timezone-name match.
  return matched.sort((a, b) => {
    const aC = (a.countries || '').toLowerCase();
    const bC = (b.countries || '').toLowerCase();
    const aN = a.value.toLowerCase();
    const bN = b.value.toLowerCase();
    const aExact = aC.split(',').map((s) => s.trim()).includes(q);
    const bExact = bC.split(',').map((s) => s.trim()).includes(q);
    if (aExact && !bExact) return -1;
    if (!aExact && bExact) return  1;
    const aStarts = aC.split(',').some((s) => s.trim().startsWith(q));
    const bStarts = bC.split(',').some((s) => s.trim().startsWith(q));
    if (aStarts && !bStarts) return -1;
    if (!aStarts && bStarts) return  1;
    if (aN.startsWith(q) && !bN.startsWith(q)) return -1;
    if (!aN.startsWith(q) && bN.startsWith(q)) return  1;
    return 0;
  });
};

const _timeDifference = (timezone) => {
  if (!timezone) return '';
  try {
    const now        = new Date();
    const userOffset = -now.getTimezoneOffset() / 60;
    const tzDate     = new Date(now.toLocaleString('en-US', { timeZone: timezone }));
    const tzOffset   = (tzDate.getTime() - now.getTime()) / (1000 * 60 * 60);
    const diff       = Math.round(tzOffset - userOffset);
    if (diff === 0)   return 'Same as your timezone';
    if (diff > 0)     return `${diff}h ahead of your timezone`;
    return `${Math.abs(diff)}h behind your timezone`;
  } catch (err) {
    return '';
  }
};

const TimezonePicker = ({
  value,
  onChange,
  label = 'Timezone',
  placeholder = 'Search timezone…',
  size = 'small',
  showDifference = true,
  sx,
  textFieldProps,
}) => {
  const options = useMemo(() => buildTimezoneOptions(), []);
  const selected = options.find((opt) => opt.value === value) || null;
  const diff = showDifference ? _timeDifference(value) : '';

  return (
    <Box sx={sx}>
      <Autocomplete
        options={options}
        value={selected}
        onChange={(_, next) => onChange?.(next?.value || '')}
        getOptionLabel={(opt) => opt?.label || ''}
        isOptionEqualToValue={(opt, val) => opt?.value === val?.value}
        filterOptions={_filter}
        size={size}
        renderInput={(params) => (
          <TextField {...params} label={label} placeholder={placeholder} {...textFieldProps} />
        )}
      />
      {showDifference && diff && (
        <Box
          sx={{
            mt: 0.75,
            px: 1.25,
            py: 0.75,
            backgroundColor: '#f0f9ff',
            borderRadius: 1,
            border: '1px solid #bae6fd',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Typography variant="caption" sx={{ color: '#0369a1', fontWeight: 600, fontSize: '0.72rem' }}>
            ⏰ {diff}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default TimezonePicker;
